import { BCMSCurrency, BCMSDefaultModel, BCMSRecord, Link, ModalOpen, useWorkflowStep } from "@bcm-dev/bcms-vite";
import { DatePickerView } from "compid/ui/DatePicker";
import { getDefaultEdit, getWorkflowSteps } from "compid/workflows/EditPaymentWorkflow";
import { Account, Biller, Payment } from "model";
import React from "react";
import { SVGAsset } from "res";

import CustomSelect, { CustomSelectItem } from "compid/custom-ui/CustomSelect";
import ButtonContainer from "compid/shared/containers/ButtonContainer";
import PageLayoutContainer from "compid/shared/containers/PageLayoutContainer";
import CenterColumnContainer from "compid/shared/containers/PageLayoutContainer/containers/CenterColumnContainer";
import PageView from "compid/shared/containers/PageViewContainer";
import PaidBillerView from "compid/shared/views/BillerView/components/views/PaidBillerView";
import TableView, { TableRowView } from "compid/shared/views/TableView";
import UIAmountInput from "compid/ui/UIAmountInput";
import UIButton from "compid/ui/UIButton";

import UILimitedCharacterInput from "compid/ui/UILimitedCharacterInput";
import "./styles.css";

/**
 * @function EditPaymentPage
 * @return {React.Component}
 * @exported true
 * @description The main component for EditPaymentPage
 */
export default function EditPaymentPage(props){
  const workflowStep = useWorkflowStep(getDefaultEdit, getWorkflowSteps);
  const { payment, bill, paymentAmount } = workflowStep.getStepState;
  // Update the amount of the bill to match the payment, so the PaidBillerView shows the correct amount
  const paymentAmountFormatted = paymentAmount.format();
  const billAmountFormatted = bill.amount.format();

  const updateDate = value => workflowStep.update(["payment", "date"], value);
  const updateAmount = value => workflowStep.update(["paymentAmount", "amount"], value.replace("$", ""));
  const updateFromAccount = value => workflowStep.update(["payment", "from"], value);
  const updateMemo = value => workflowStep.update([ "payment", "memo" ], value);

  const accountNumber = payment.from.number;
  const paymentFromID = payment.from.id;
  const isPaper = payment.to.paymentType.toLowerCase() === "paper";
  const isElectronic = payment.to.paymentType.toLowerCase() === "electronic";
  const isVirtualCard = payment.to.paymentType === "virtualCard";
  const { branding } = workflowStep.model;
  const hasRealTimeGoodFunds = Payment.hasRealTimeGoodFunds(payment.to.paymentType, branding);

  // dontSave :: navigate back to the "view payment" page.
  const dontSave = () => workflowStep.navigate(-1);

  const customSelectItems = Account.initAll().map((account) => {
    return (
      <CustomSelectItem key={ account.id } value={ account.id }>{ account.name + " " + account.number }</CustomSelectItem>
    );
  });

  const getFee =  ()=>{
      if(feeIsSelected) {
        return isPaper
              ? BCMSCurrency.init(options.overnight_check_fee_rxp22 === "undefined" ? "14.95" : options.overnight_check_fee_rxp22).amount
              : BCMSCurrency.init(options.same_day_fee_rxp22 === "undefined" ? "9.95" : options.same_day_fee_rxp22).amount;

      }
      return 0;
  }

  const dueDate = bill.status.toLowerCase() === "due" ? bill.date : undefined;
  // This section determines if a date with a fee is selected and if the branding should show fees
  const options = workflowStep.model.branding.options;

  const feeIsSelected = Payment.getIfDateHasFee(payment.date, payment.to.paymentType, workflowStep.model.branding);
  const showFeeLabel = isPaper ? options.overnight_check_rxp22 : options.same_day_payment_rxp22;
  let feeLabel = showFeeLabel === "true"
      ? feeIsSelected
          ? "Rush delivery selected" : "Rush delivery available"
      : "";

  if((hasRealTimeGoodFunds && isPaper) || isVirtualCard) {
      feeLabel = "";
  }

  // "paper" and "electronic" billers show fees, but not "virtualCard" billers.
  const showFees = isVirtualCard ? "false" : "true";

  // START: Create the "Withdrawn on..." message. 
  let withdrawnOnDateLabel = "";
  let withdrawnOnDate = Payment.getWithdrawOnDate(payment.date, payment.to.paymentType, workflowStep.model.branding);
  payment.withdrawnOnDate = withdrawnOnDate;
  withdrawnOnDateLabel = withdrawnOnDate.format("M d");
  const withdrawnLabel = hasRealTimeGoodFunds ? <p>Withdrawn on { withdrawnOnDateLabel }</p> : undefined;
  // END: Create the "Withdrawn on..." message. 

  const availableBalanceText = `Available balance: ${ payment.from.balance.format() }`;
  let fundsMessage1 = ``;
  let fundsMessage2 = ``;

  if(hasRealTimeGoodFunds) {
    fundsMessage1 = `The funds ${ paymentAmountFormatted } will be withdrawn from your account ${ accountNumber } immediately.`;
    fundsMessage2 = `The funds ${ billAmountFormatted } will be refunded to your account ${ accountNumber }.`;
  }

  const paperBillerDatePickerSubtext = hasRealTimeGoodFunds
      ? withdrawnLabel
      : <>
          <div className="info-text-container flex-container flex-horizontal align-top">
            <SVGAsset id="UpsellIcon" className="icon info-icon blue-icon set-width" />
            <p className="sub-text">Your check may be cashed, and the money withdrawn from your account, before, on, or after { payment.formatDate }.</p>
        </div>
      </>;

  const onSubmit = evt => {
    let { payment, paymentAmount } = workflowStep.getStepState ?? {};

    const defaultPayment = BCMSDefaultModel.payment();

    payment.amount = paymentAmount;
    payment = Payment.init(BCMSDefaultModel.mergeRecords(payment, defaultPayment));

    BCMSRecord.batch([
      Payment.update(payment)
    ], workflowStep.globalController);

    workflowStep.workflow.nextStep({ ...workflowStep.getStepState, payment });
  }

  return (
    <PageView className="edit-payment-page">
      <p className="page-sub-header semi-bold-text">EDIT PAYMENT</p>

      <PageLayoutContainer>
        <div className="flex-container flex-vertical flex-width">
          <div className="flex-height flex-container flex-vertical upper-content-container">
              {/* BillerView needs hook up - Has to comment it out as it caused issues on the page  */}
              <PaidBillerView biller={ payment.to } bill={ bill } />
          </div>

          <div className="flex-height flex-width flex-container all-center">
            <CenterColumnContainer>
              <div className="form-group flex-container flex-vertical">
                <div className="amount-input-container">
                  <UIAmountInput className="amount-input" placeHolder="Amount" onChange={ updateAmount } value={ paymentAmount.format() } dataPoint="payment_amount"/>
                </div>

                { isPaper && <div className="form-row flex-height">
                        <DatePickerView dataPoint="Review_Pay_Date" setDate={ payment.date } dateLabel="Estimated delivery" rightLabel={ feeLabel } onChange={ updateDate } showFees={ showFees } billerType={ payment.to.paymentType } dueDate={ dueDate }>
                            <div className="flex-container flex-horizontal align-left">
                              <span className="sub-text">{ paperBillerDatePickerSubtext }</span>
                            </div>
                        </DatePickerView>
                    </div> }

                { !isPaper && <div className="form-row flex-height">
                        <DatePickerView setDate={ payment.date } dateLabel="Deliver by" rightLabel={ feeLabel } onChange={ updateDate }
                        showFees={ showFees } billerType={ payment.to.paymentType } dueDate={ dueDate } dataPoint="Review_And_Pay">
                            <div className="flex-container flex-horizontal align-center date-helper-text">
                              <span className="sub-text">{ withdrawnLabel }</span>
                            </div>
                        </DatePickerView> 
                    </div> 
                }
                
                <div className="form-row pay-from-row hide-dropdown-arrow">
                  <CustomSelect label="Pay from" helpText={""} value={ paymentFromID } onChange= { updateFromAccount } dataPoint="account_select">
                      { customSelectItems }
                  </CustomSelect>
                  { hasRealTimeGoodFunds &&
                      <>
                          <div className="flex-container flex-horizontal align-center available-balance gray-text">
                              <span className="sub-text">{ availableBalanceText }</span>
                          </div>
                          <div className="flex-container flex-horizontal align-center select-lower-text">
                              <span className="sub-text">{ fundsMessage1 }</span>
                          </div>
                          <div className="flex-container flex-horizontal align-center select-lower-text">
                              <span className="sub-text">{ fundsMessage2 }</span>
                          </div>
                      </>
                  }
                  
                </div>

                { isPaper && <UILimitedCharacterInput placeHolder="Memo" value={ payment.memo } onChange={ updateMemo } className="memo-input" dataPoint="Edit_Payment_Memo" /> }  

                { isVirtualCard &&
                    <div className="form-row flex-height">
                        <div className="flex-container flex-horizontal align-center">
                            <span className="sub-text">{ Biller.getVirtualCardDisclaimer(true) }</span>
                        </div>
                    </div>
                }
              </div>
              {feeIsSelected &&
                <div className="fee-table">
                <TableView>
                  <TableRowView label="Delivery Fee" data={BCMSCurrency.init(getFee()).format() } />
                </TableView>
                </div>

                }
              <ButtonContainer className="form-button-container">
                  <UIButton className="secondary-button" onClick={ dontSave }  dataPoint="dont_save_changes">Don't save changes</UIButton>
                  <UIButton className="primary-button" dataPoint="save_changes" onClick={ onSubmit }>Save changes</UIButton>
                </ButtonContainer>
                
                <Link className="text-center semi-bold-text cancel-link link-button" dataPoint="cancel_payment">
                  <ModalOpen id="CancelPaymentModal">Cancel payment</ModalOpen>
                </Link>
            </CenterColumnContainer>
          </div>
        </div>
      </PageLayoutContainer>
    </PageView>
  );
}